import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { UserService } from 'src/app/services/user.service';
import { Subject, takeUntil } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Role } from 'src/app/models/role.model';
import { ToastrService } from 'ngx-toastr';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';
import { LoadingService } from 'src/app/services/loading.service';
import { User } from 'src/app/models/user.model';
import * as intlTelInput from 'intl-tel-input';
import { telErrorMap, telUtilsScript } from 'src/app/dashboard/helpers/tel-errors';
import { CustomerService } from 'src/app/services/customer.service';
import { CompanyLite } from 'src/app/models/customer.model';
@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.scss'],
})
export class EditUserComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Input() user: any;
  @Input() roles: Role[] = [];
  @Input() isBodyLocked!: boolean;
  @Output() modalState = new EventEmitter<{
    isDelete: boolean;
    isUpdate: boolean;
  }>();
  @ViewChild('editModal') editModalEl!: ElementRef;
  selectedRole!: Role;
  isRoleChanged = false;
  userLastActivity!: string;
  availableRoles!: Role[];

  // ECDEV-256: a user may belong to several companies. selectableCompanies is narrowed to the
  // company type already chosen, because mixing a Customer with a Cold Storage would flip the
  // company.type claim on every switch — the server rejects it too.
  allCompanies: CompanyLite[] = [];
  selectedCompanyIds: string[] = [];
  defaultCompanyId: string | null = null;
  private initialCompanyIds: string[] = [];
  private initialDefaultCompanyId: string | null = null;

  private destroy = new Subject<boolean>();

  userForm = new FormGroup({
    firstName: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[ a-zA-Z\-\']+$/),
    ]),
    lastName: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[ a-zA-Z\-\']+$/),
    ]),
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
    ]),
    phoneNumber: new FormControl('', [Validators.required]),
    receiveEmailNotificationWhenOrderDetailsChanged: new FormControl(),
    receiveEmailNotificationWhenOrderFileUploaded: new FormControl(),
    receiveEmailNotificationWhenOrderStageChanged: new FormControl(),
  });

  userTelData: any;
  telError!: string;
  isTelCustomError!: boolean;

  constructor(
    private userService: UserService,
    private toastr: ToastrService,
    private loadingService: LoadingService,
    private customerService: CustomerService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    setTimeout(() => {
      if (changes['isBodyLocked'].currentValue) {
        disableBodyScroll(this.editModalEl.nativeElement);
      }
    }, 0);
    this.getUserData();
  }

  ngOnInit(): void {
    this.selectedRole = this.user.role;
    this.filterRoles();

    // ECDEV-256: only meaningful for company-scoped users; CM staff have no company.
    if (this.canEditCompanies) {
      this.loadUserCompanies();
      this.loadSelectableCompanies();
    }

    this.userForm.patchValue({
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      email: this.user.email,
      receiveEmailNotificationWhenOrderDetailsChanged:
        this.user.receiveEmailNotificationWhenOrderDetailsChanged,
      receiveEmailNotificationWhenOrderFileUploaded:
        this.user.receiveEmailNotificationWhenOrderFileUploaded,
      receiveEmailNotificationWhenOrderStageChanged:
        this.user.receiveEmailNotificationWhenOrderStageChanged,
    });
  }

  ngAfterViewInit(): void {
    this.createPhoneElement();
  }

  createPhoneElement() {
    const telInputEl = document.getElementById('phoneNumber');

    if (telInputEl) {
      this.userTelData = intlTelInput(telInputEl, {
        initialCountry: 'US',
        separateDialCode: true,
        utilsScript: telUtilsScript,
      });
      if (this.user.phoneNumber) {
        this.userTelData.setNumber(this.user.phoneNumber);
      }
    }
  }

  validateTel() {
    if (!this.userTelData.isPossibleNumber()) {
      this.isTelCustomError = true;
      this.telError = telErrorMap[this.userTelData.getValidationError()];

      if (!this.telError) {
        this.telError = 'Invalid number';
      }
    } else {
      this.isTelCustomError = false;
    }
  }

  filterRoles() {
    if (
      (this.user.role.parentRole !== null &&
        this.user.role.parentRole.name === 'ColdStorage') ||
      this.user.role.name === 'ColdStorage'
    ) {
      this.availableRoles = this.roles.filter(
        (r: Role) =>
          (r.parentRole !== null && r.parentRole.name === 'ColdStorage') ||
          r.name === 'ColdStorage'
      );
    } else if (
      (this.user.role.parentRole !== null &&
        this.user.role.parentRole.name === 'Customers') ||
      this.user.role.name === 'Customers'
    ) {
      this.availableRoles = this.roles.filter(
        (r: Role) =>
          (r.parentRole !== null && r.parentRole.name === 'Customers') ||
          r.name === 'Customers'
      );
    } else if (
      (this.user.role.parentRole !== null &&
        this.user.role.parentRole.name === 'CmpAdmin') ||
      this.user.role.name === 'CmpAdmin'
    ) {
      this.availableRoles = this.roles.filter(
        (r: Role) =>
          (r.parentRole !== null && r.parentRole.name === 'CmpAdmin') ||
          r.name === 'CmpAdmin'
      );
    } else {
      this.availableRoles = [...this.roles];
    }
  }

  getUserData() {
    this.userService
      .getUserById(this.user.id)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (resp: User) => {
          this.userLastActivity = resp.lastActivityOn ?? '';
        },
      });
  }

  changeRole() {
    if (this.selectedRole.id !== this.user.role.id) {
      this.isRoleChanged = true;
    } else {
      this.isRoleChanged = false;
    }
  }

  // ---------------------------------------------------------------- ECDEV-256

  /** CM staff have no company at all, so there is nothing to assign. */
  get canEditCompanies(): boolean {
    return !!this.user?.companyId;
  }

  get selectedCompanies(): CompanyLite[] {
    return this.allCompanies.filter((c) =>
      this.selectedCompanyIds.includes(c.id)
    );
  }

  /**
   * Once one company is picked, only companies of that type remain on offer — the same rule the
   * server enforces, surfaced before the user hits a rejection.
   */
  get selectableCompanies(): CompanyLite[] {
    const chosenType = this.selectedCompanies[0]?.type;

    return chosenType === undefined
      ? this.allCompanies
      : this.allCompanies.filter((c) => c.type === chosenType);
  }

  get areCompaniesChanged(): boolean {
    if (!this.canEditCompanies) {
      return false;
    }

    const sameSet =
      this.selectedCompanyIds.length === this.initialCompanyIds.length &&
      [...this.selectedCompanyIds]
        .sort()
        .every((id, i) => id === [...this.initialCompanyIds].sort()[i]);

    return !sameSet || this.defaultCompanyId !== this.initialDefaultCompanyId;
  }

  /** A company-scoped user must keep at least one company, or they would see nothing. */
  get isCompanySelectionValid(): boolean {
    return !this.canEditCompanies || this.selectedCompanyIds.length > 0;
  }

  changeCompanies() {
    // Keep the default inside the selection; picking a new set invalidates an old default.
    if (
      !this.defaultCompanyId ||
      !this.selectedCompanyIds.includes(this.defaultCompanyId)
    ) {
      this.defaultCompanyId = this.selectedCompanyIds[0] ?? null;
    }
  }

  loadUserCompanies() {
    if (!this.user?.id) {
      return;
    }

    this.userService
      .getUserCompanies(this.user.id)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (companies) => {
          this.selectedCompanyIds = companies.map((c) => c.id);
          this.initialCompanyIds = [...this.selectedCompanyIds];
          this.defaultCompanyId =
            companies.find((c) => c.isDefault)?.id ??
            this.selectedCompanyIds[0] ??
            null;
          this.initialDefaultCompanyId = this.defaultCompanyId;
        },
      });
  }

  loadSelectableCompanies() {
    this.customerService
      .getCompaniesLite({
        pageNumber: 1,
        pageSize: 1000,
        orderBy: ['name'],
      } as any)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (resp) => (this.allCompanies = resp.data),
      });
  }

  // --------------------------------------------------------------------------

  editUserRole() {
    const payload = {
      // ECDEV-256: the whole membership set plus which one they land in at login.
      companyId: this.canEditCompanies
        ? this.defaultCompanyId
        : this.user.companyId,
      companyIds: this.canEditCompanies ? this.selectedCompanyIds : undefined,
      roleId: this.selectedRole.id,
    };

    this.userService
      .updateUserRole(this.user.id, payload)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: () => {
          this.modalState.emit({ isDelete: false, isUpdate: true });
          this.toastr.success('User role updated', 'Success');
          this.isRoleChanged = false;
        },
        error: (err) => {
          this.modalState.emit({ isDelete: false, isUpdate: true });
          this.isRoleChanged = false;
        },
      });
  }

  editUser() {
    const payload = {
      firstName: this.userForm.controls.firstName.value ?? '',
      lastName: this.userForm.controls.lastName.value ?? '',
      phoneNumber: this.userTelData.getNumber() ?? '',
      receiveEmailNotificationWhenOrderDetailsChanged:
        this.userForm.controls.receiveEmailNotificationWhenOrderDetailsChanged
          .value,
      receiveEmailNotificationWhenOrderFileUploaded:
        this.userForm.controls.receiveEmailNotificationWhenOrderFileUploaded
          .value,
      receiveEmailNotificationWhenOrderStageChanged:
        this.userForm.controls.receiveEmailNotificationWhenOrderStageChanged
          .value,
    };
    this.userService
      .editUser(this.user.id, payload)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (resp) => {
          this.modalState.emit({ isDelete: false, isUpdate: true });
          this.toastr.success('User updated', 'Success');
        },
      });
  }

  forgotPassword() {
    this.loadingService.changeLoadingStatus(true);
    const payload = {
      email: this.user.email ?? '',
    };
    this.userService
      .forgotPassword(payload)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: () => {
          this.loadingService.changeLoadingStatus(false);
          this.toastr.success(
            'Email with instructions was sent to user',
            'Success'
          );
        },
        error: () => {
          this.loadingService.changeLoadingStatus(false);
        },
      });
  }

  deleteAccount() {
    this.modalState.emit({ isDelete: true, isUpdate: false });
  }

  closeModal() {
    this.modalState.emit({ isDelete: false, isUpdate: false });
    enableBodyScroll(this.editModalEl.nativeElement);
  }

  backdropClick(event: Event) {
    if (event.target === event.currentTarget) {
      this.modalState.emit({ isDelete: false, isUpdate: false });
      enableBodyScroll(this.editModalEl.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
    clearAllBodyScrollLocks();
  }
}
